import { useQuery } from "@tanstack/react-query";
import { css } from "styled-system/css";
import {
  Columns,
  Figure,
  RankedBars,
  StackedBars,
  ed as edc,
  type ColumnPoint,
  type StackKey,
} from "~/components/charts";
import { api } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import { useDashboardData } from "./useDashboardData";
import { ActionItems } from "./components/ActionItems";
import { DailyBriefing } from "./components/DailyBriefing";
import { VelocityPulse } from "./components/VelocityPulse";
import { buildWorkHeadline } from "./story";
import { ed, tbl } from "~/components/editorial";
import { capitalize, fmtDay, fmtNum, periodName, plural, priorName } from "~/lib/format";

interface CompanyDigestData {
  jiraBaseUrl: string | null;
  jiraInsights: {
    recentlyCompleted: Array<{
      key: string;
      summary: string;
      issueType: string;
      storyPoints: number | null;
    }>;
    inProgress: Array<{
      key: string;
      summary: string;
      issueType: string;
      assignee: { name: string } | null;
    }>;
    issuesByType: Array<{ type: string; count: number }>;
  };
  projectSnapshots: Array<{
    id: string;
    key: string;
    name: string;
    issuesResolved: number;
    issuesInProgress: number;
    storyPointsCompleted: number;
    activeContributors: number;
  }>;
  collaborationStats: {
    mostActiveReviewer: { name: string; count: number } | null;
    mostCollaborativePair: {
      developer1: string;
      developer2: string;
      interactions: number;
    } | null;
    crossProjectReviews: number;
  };
  recentActivity: Array<{
    type: "pr_merged" | "review" | "issue_resolved";
    title: string;
    description: string;
    developer: { name: string };
    timestamp: string;
    metadata?: { jiraKey?: string };
  }>;
}

const ACTIVITY_VERB: Record<CompanyDigestData["recentActivity"][number]["type"], string> = {
  pr_merged: "Merged",
  review: "Reviewed",
  issue_resolved: "Resolved",
};

/** Categories past this many fold into "Everything else" in the trend. */
const TREND_KEYS = 4;
const TREND_GRAYS = [edc.markStrong, edc.mark, edc.rule] as const;

function formatTimeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return "now";
}

function shortDate(d: string): string {
  const dt = new Date(`${d}T12:00:00`);
  return Number.isNaN(dt.getTime())
    ? d
    : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Fill the period's days so a sparse daily series reads as a calendar. */
function fillDays(points: Array<{ date: string; value: number }>, from: Date, to: Date): ColumnPoint[] {
  const byDate = new Map(points.map((p) => [p.date, p.value]));
  const out: ColumnPoint[] = [];
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = Math.min(to.getTime(), Date.now());
  // Guard against a year view drawing hundreds of hairline columns
  if ((end - d.getTime()) / 86_400_000 > 120) return points.map((p) => ({ key: p.date, value: p.value }));
  while (d.getTime() <= end) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    out.push({ key, value: byDate.get(key) ?? 0 });
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export function DashboardPage() {
  const { range, window: periodWin } = usePeriod();
  const now = periodName(periodWin.label);
  const prior = priorName(periodWin.prevLabel);

  const { data: digestData } = useQuery({
    queryKey: ["company-digest", range],
    queryFn: () =>
      api.get<{ data: CompanyDigestData }>(
        `/metrics/company-digest?range=${range}`,
      ),
    staleTime: 5 * 60 * 1000,
  });

  const {
    topContributors,
    workCategoryBreakdown,
    workCategoryStats,
    workCategoryTrend,
    storyToCode,
    storyPointsTrend,
  } = useDashboardData();

  const digest = digestData?.data;
  const issueUrl = (key: string) =>
    digest?.jiraBaseUrl ? `${digest.jiraBaseUrl}/browse/${key}` : null;

  const today = new Date();
  const stamp = `${today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}`;

  // --- The work: categories, top one in emphasis
  const categories = workCategoryBreakdown
    .filter((c) => c.value > 0)
    .sort((a, b) => b.value - a.value);
  const workHeadline = buildWorkHeadline(categories);
  const topCategory = categories[0];

  // Trend keys: the period's top category in emphasis, the next few in
  // grays, the rest folded together.
  const trendKeys: StackKey[] = [];
  const folded: string[] = [];
  if (workCategoryTrend) {
    const totals = workCategoryTrend.categories
      .map((c) => ({
        ...c,
        total: workCategoryTrend.trend.reduce(
          (s, d) => s + ((d[c.category as keyof typeof d] as number) || 0),
          0,
        ),
      }))
      .filter((c) => c.total > 0)
      .sort((a, b) => b.total - a.total);
    totals.forEach((c, i) => {
      if (i < TREND_KEYS) {
        trendKeys.push({
          key: c.category,
          label: c.label,
          color: i === 0 ? edc.emph : (TREND_GRAYS[i - 1] ?? edc.rule),
        });
      } else {
        folded.push(c.category);
      }
    });
    if (folded.length > 0) {
      trendKeys.push({ key: "__rest", label: "Everything else", color: edc.grid });
    }
  }
  const trendRows =
    workCategoryTrend?.trend.map((d) => {
      const values: Record<string, number> = {};
      for (const k of trendKeys) {
        if (k.key !== "__rest") values[k.key] = (d[k.key as keyof typeof d] as number) || 0;
      }
      values.__rest = folded.reduce(
        (s, f) => s + ((d[f as keyof typeof d] as number) || 0),
        0,
      );
      return { id: d.date, label: `Week of ${shortDate(d.date)}`, values };
    }) ?? [];

  const pointsDays = fillDays(storyPointsTrend, periodWin.from, periodWin.to);
  const pointsTotal = storyPointsTrend.reduce((s, p) => s + p.value, 0);

  const collab = digest?.collaborationStats;

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <header className={ed.masthead}>
          <span className={ed.mastTitle}>Aura · The front page</span>
          <span className={ed.mastMeta}>
            {stamp} · {periodWin.label} vs. {periodWin.prevLabel}
          </span>
        </header>

        <VelocityPulse />

        <DailyBriefing label="I. The briefing" />

        <ActionItems label="II. Needs attention" />

        {/* III. The work */}
        {(categories.length > 0 || trendRows.length > 0) && (
          <section>
            <div className={ed.sect}>III. The work</div>
            <h2 className={ed.h2}>{workHeadline ?? `What the team merged in ${now}`}</h2>
            {workCategoryStats && (
              <p className={ed.prose}>
                Of <span className="num">{fmtNum(workCategoryStats.total)}</span> merged PRs,{" "}
                <span className="num">{fmtNum(workCategoryStats.linkedToJira)}</span> named a JIRA
                ticket, a coverage of{" "}
                <span className="num">{fmtNum(workCategoryStats.linkedPercentage)}%</span>. The
                category comes from the linked issue type when there is one, and from the branch
                and title when there isn't.
              </p>
            )}
            <div className={ed.halves}>
              {categories.length > 0 && (
                <Figure
                  title={`Merged PRs by kind of work, ${now}`}
                  sub={topCategory ? `${topCategory.name} in color.` : undefined}
                  method="Merged PRs in the period, one category each. Categories from the linked JIRA issue type, else branch and title patterns (feature, fix, refactor, docs, release, infra)."
                  table={{
                    columns: ["Category", "PRs", "Share"],
                    rows: categories.map((c) => [c.name, c.value, `${fmtNum(c.percentage)}%`]),
                  }}
                >
                  <RankedBars
                    rows={categories.map((c, i) => ({
                      id: c.category,
                      label: c.name,
                      value: c.value,
                      emphasis: i === 0,
                    }))}
                    ariaLabel="Merged PRs by work category"
                    width={560}
                  />
                </Figure>
              )}
              {trendRows.length > 0 && (
                <Figure
                  title="The mix, week by week"
                  sub={trendKeys[0] ? `Share of each week's merged PRs. ${trendKeys[0].label} in color.` : undefined}
                  method="Merged PRs per calendar week, split by category. Bars are shares of that week; the number is the week's count."
                >
                  <StackedBars
                    rows={trendRows}
                    keys={trendKeys}
                    mode="share"
                    ariaLabel="Work category share by week"
                  />
                </Figure>
              )}
            </div>
          </section>
        )}

        {/* IV. Tickets to code */}
        <section>
          <div className={ed.sect}>IV. Tickets to code</div>
          <h2 className={ed.h2}>
            {storyToCode && storyToCode.summary.totalLinkedIssues > 0
              ? `${capitalize(plural(storyToCode.summary.totalLinkedIssues, "JIRA issue"))} resolved with linked PRs in ${now}`
              : `What moved in JIRA in ${now}`}
          </h2>

          {storyToCode && storyToCode.summary.totalLinkedIssues > 0 && (
            <>
              <dl className={styles.facts}>
                <Fact label="Linked issues" value={fmtNum(storyToCode.summary.totalLinkedIssues)} note={`${fmtNum(storyToCode.summary.totalPrs)} PRs`} />
                <Fact label="PRs per ticket" value={fmtNum(storyToCode.metrics.avgPrsPerTicket, 1)} note={storyToCode.insights.complexityIndicator} />
                <Fact label="Reviews per ticket" value={fmtNum(storyToCode.metrics.avgReviewCyclesPerTicket, 1)} note={storyToCode.insights.reviewHealth} />
                <Fact label="Ticket to merge" value={`${fmtNum(storyToCode.metrics.avgFullCycleTimeDays, 1)}d`} note="average" />
              </dl>
              <p className={ed.method}>
                JIRA issues resolved in the period that have at least one linked PR (the key in
                the PR title or branch). Ticket to merge runs from issue creation to its last
                merged PR.
              </p>
            </>
          )}

          {pointsTotal > 0 && (
            <div className={styles.spaced}>
              <Figure
                title={`${fmtNum(pointsTotal)} story points resolved in ${now}`}
                sub="Points on JIRA issues, by the day they were resolved."
                method="Sum of story points on issues resolved each day. Issues without an estimate count as zero."
              >
                <Columns
                  points={pointsDays}
                  unit="points"
                  ariaLabel="Story points resolved per day"
                  tone="strong"
                  formatKey={fmtDay}
                  format={String}
                  tickFormat={(t) => String(Math.round(t * 10) / 10)}
                  height={140}
                  margin={{ t: 14, l: 34 }}
                  maxLabels={8}
                />
              </Figure>
            </div>
          )}

          {digest && (
            <div className={`${ed.halves} ${styles.spaced}`}>
              <IssueTable
                title="In progress"
                issues={digest.jiraInsights.inProgress.slice(0, 6).map((i) => ({
                  key: i.key,
                  summary: i.summary,
                  issueType: i.issueType,
                  right: i.assignee?.name ?? "Unassigned",
                }))}
                rightLabel="Assignee"
                rightIsNum={false}
                issueUrl={issueUrl}
                empty="Nothing in progress."
              />
              <IssueTable
                title="Recently done"
                issues={digest.jiraInsights.recentlyCompleted.slice(0, 6).map((i) => ({
                  key: i.key,
                  summary: i.summary,
                  issueType: i.issueType,
                  right: i.storyPoints ? fmtNum(i.storyPoints) : "–",
                }))}
                rightLabel="Pts"
                rightIsNum
                issueUrl={issueUrl}
                empty="Nothing resolved yet."
              />
            </div>
          )}

          {digest && digest.projectSnapshots.length > 0 && (
            <div className={styles.spaced}>
              <h3 className={ed.h3}>By project</h3>
              <div className={tbl.scrollPlain}><table className={tbl.simple}>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Project</th>
                    <th data-num>Resolved</th>
                    <th data-num>In progress</th>
                    <th data-num>Points</th>
                    <th data-num>People</th>
                  </tr>
                </thead>
                <tbody>
                  {digest.projectSnapshots.slice(0, 8).map((p) => (
                    <tr key={p.id}>
                      <td data-id>{p.key}</td>
                      <td>{p.name}</td>
                      <td data-num>{fmtNum(p.issuesResolved)}</td>
                      <td data-num>{fmtNum(p.issuesInProgress)}</td>
                      <td data-num>{fmtNum(p.storyPointsCompleted)}</td>
                      <td data-num>{fmtNum(p.activeContributors)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
              <p className={ed.method}>
                JIRA projects with activity in {now}, busiest first as the company digest ranks
                them. Points are story points on resolved issues.
              </p>
            </div>
          )}
        </section>

        {/* V. People */}
        <section>
          <div className={ed.sect}>V. People</div>
          <h2 className={ed.h2}>
            {topContributors[0]
              ? `${topContributors[0].name} merged the most PRs in ${now}, with ${fmtNum(topContributors[0].value)}`
              : `Who shipped in ${now}`}
          </h2>
          {collab && (
            <p className={ed.prose}>
              {collab.mostActiveReviewer && (
                <>
                  {collab.mostActiveReviewer.name} reviewed the most, with{" "}
                  <span className="num">{fmtNum(collab.mostActiveReviewer.count)}</span> reviews.{" "}
                </>
              )}
              {collab.mostCollaborativePair && (
                <>
                  {collab.mostCollaborativePair.developer1} and{" "}
                  {collab.mostCollaborativePair.developer2} worked together most, across{" "}
                  <span className="num">{fmtNum(collab.mostCollaborativePair.interactions)}</span>{" "}
                  reviews of each other's PRs.{" "}
                </>
              )}
              <span className="num">{fmtNum(collab.crossProjectReviews)}</span>{" "}
              {collab.crossProjectReviews === 1 ? "review" : "reviews"} crossed project lines.
            </p>
          )}
          <div className={ed.twoUp}>
            <Figure
              title="Merged PRs, top five authors"
              sub={`${capitalize(now)}. The leader in color.`}
              method={`PRs merged in the period, by author. Compare with ${prior} on the Developers page.`}
            >
              {topContributors.length > 0 ? (
                <RankedBars
                  rows={topContributors.slice(0, 6).map((c, i) => ({
                    id: c.name,
                    label: c.name,
                    value: c.value,
                    emphasis: i === 0,
                  }))}
                  ariaLabel="Merged PRs by author"
                  width={560}
                />
              ) : (
                <p className={ed.empty}>No merged PRs in this period.</p>
              )}
            </Figure>

            <div>
              <h3 className={ed.h3}>The wire</h3>
              <p className={styles.sub}>Latest merges, reviews and resolved issues.</p>
              {digest && digest.recentActivity.length > 0 ? (
                <table className={tbl.simple}>
                  <tbody>
                    {digest.recentActivity.slice(0, 8).map((a, idx) => (
                      <tr key={idx}>
                        <td className={styles.when}>{formatTimeAgo(a.timestamp)}</td>
                        <td className={tbl.clipCell}>
                          <div className={styles.wireWho}>
                            <b>{a.developer.name}</b> {ACTIVITY_VERB[a.type].toLowerCase()}
                            {a.metadata?.jiraKey && (
                              <span className={styles.wireKey}> {a.metadata.jiraKey}</span>
                            )}
                          </div>
                          <div className={styles.wireTitle} title={a.title}>
                            {a.title}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className={ed.empty}>No recent activity.</p>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Fact({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
      {note && <dd className={styles.factNote}>{note}</dd>}
    </div>
  );
}

function IssueTable({
  title,
  issues,
  rightLabel,
  rightIsNum,
  issueUrl,
  empty,
}: {
  title: string;
  issues: Array<{ key: string; summary: string; issueType: string; right: string }>;
  rightLabel: string;
  rightIsNum: boolean;
  issueUrl: (key: string) => string | null;
  empty: string;
}) {
  return (
    <div>
      <h3 className={ed.h3}>{title}</h3>
      {issues.length === 0 ? (
        <p className={ed.empty}>{empty}</p>
      ) : (
        <div className={tbl.scrollPlain}><table className={tbl.simple}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Type</th>
              <th>Summary</th>
              <th data-num={rightIsNum || undefined}>{rightLabel}</th>
            </tr>
          </thead>
          <tbody>
            {issues.map((i) => {
              const url = issueUrl(i.key);
              return (
                <tr key={i.key}>
                  <td data-id>
                    {url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {i.key}
                      </a>
                    ) : (
                      i.key
                    )}
                  </td>
                  <td>
                    <span className={tbl.tagOutline}>{i.issueType}</span>
                  </td>
                  <td className={tbl.clipCell} title={i.summary}>
                    {i.summary}
                  </td>
                  <td data-num={rightIsNum || undefined} className={rightIsNum ? undefined : styles.nowrap}>
                    {i.right}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

const styles = {
  spaced: css({ mt: "28px" }),
  sub: css({
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink2",
    m: 0,
    mb: "8px",
  }),
  nowrap: css({ whiteSpace: "nowrap", color: "ed.ink2" }),
  facts: css({
    display: "grid",
    gridTemplateColumns: { base: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
    borderTopWidth: "2px",
    borderTopStyle: "solid",
    borderColor: "ed.ink",
    m: 0,
  }),
  fact: css({
    px: "12px",
    pt: "10px",
    pb: "12px",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
    minWidth: 0,
  }),
  factLabel: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    mb: "6px",
  }),
  factValue: css({
    m: 0,
    fontFamily: "mono",
    fontVariantNumeric: "tabular-nums",
    fontSize: "24px",
    fontWeight: 600,
    letterSpacing: "-0.02em",
    lineHeight: 1.1,
  }),
  factNote: css({
    m: 0,
    mt: "4px",
    fontFamily: "sans",
    fontSize: "11.5px",
    color: "ed.ink3",
    lineHeight: 1.35,
  }),
  when: css({
    fontFamily: "mono",
    fontSize: "11.5px",
    color: "ed.ink3",
    whiteSpace: "nowrap",
    width: "64px",
  }),
  wireWho: css({
    fontSize: "12.5px",
    color: "ed.ink2",
    "& b": { color: "ed.ink", fontWeight: 600 },
  }),
  wireKey: css({ fontFamily: "mono", fontSize: "11.5px" }),
  wireTitle: css({
    fontFamily: "serif",
    fontSize: "14.5px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
};
